import React, { useMemo } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from 'recharts';
import type { SalesByGroup } from '../services/api';
import { SALES_GROUP_CHART_ORDER, normalizeSalesByGroup } from '../lib/salesGroups';

const GROUP_COLORS: Record<string, string> = {
  กาแฟ: '#78350f',
  ชา: '#047857',
  น้ำหวาน: '#c026d3',
  ขนม: '#b45309',
  อื่นๆ: '#78716c',
};

const tooltipContentStyle: React.CSSProperties = {
  background: '#1a0f0c',
  border: 'none',
  borderRadius: 12,
  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  fontSize: 13,
};

const axisStyle = { fill: '#78716c', fontSize: 12 };

interface Props {
  loading: boolean;
  /** since_7d.by_group จาก GET /admin/stats/order-sales */
  salesByGroup7d?: SalesByGroup | null;
}

export const DashboardCharts: React.FC<Props> = ({ loading, salesByGroup7d }) => {
  const totals = normalizeSalesByGroup(salesByGroup7d ?? undefined);

  const barData = useMemo(() => {
    return SALES_GROUP_CHART_ORDER.filter((cat) => cat !== 'อื่นๆ' || (totals['อื่นๆ'] ?? 0) > 0).map((ประเภท) => ({
      ประเภท,
      แก้ว: totals[ประเภท] ?? 0,
    }));
  }, [totals]);

  const totalCups = useMemo(() => barData.reduce((a, x) => a + x.แก้ว, 0), [barData]);

  if (loading) {
    return (
      <div className="dash-chart-grid">
        <div className="dash-chart-card dash-chart-card-wide dash-chart-skeleton" aria-hidden />
      </div>
    );
  }

  return (
    <div className="dash-chart-grid">
      <div className="dash-chart-card dash-chart-card-wide">
        <div className="dash-chart-card-head">
          <h3 className="dash-chart-title">เมนูกลุ่มไหนขายดี</h3>
          <p className="dash-chart-sub">
            แยกจากรายการในออเดอร์ที่ <strong>ชำระแล้ว</strong> ช่วง 7 วันล่าสุด (กาแฟ / ชา / น้ำหวาน / ขนม)
            {totalCups > 0 ? (
              <>
                {' '}
                — รวม <strong>{totalCups.toLocaleString('th-TH')} แก้ว</strong>
              </>
            ) : null}
          </p>
        </div>
        <div className="dash-chart-body">
          {totalCups === 0 ? (
            <p className="dash-chart-empty">ยังไม่มีออเดอร์ที่ชำระแล้วในช่วง 7 วันล่าสุด</p>
          ) : (
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={barData} margin={{ top: 16, right: 16, left: 4, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
                <XAxis
                  dataKey="ประเภท"
                  tick={axisStyle}
                  axisLine={{ stroke: '#d6d3d1' }}
                  tickLine={false}
                  interval={0}
                  height={36}
                />
                <YAxis allowDecimals={false} tick={axisStyle} axisLine={false} tickLine={false} width={44} />
                <Tooltip
                  contentStyle={tooltipContentStyle}
                  formatter={(value: number) => [`${value.toLocaleString('th-TH')} แก้ว`, '']}
                  labelFormatter={(label) => `หมวด ${label}`}
                  labelStyle={{ color: '#fde68a', fontWeight: 600 }}
                />
                <Bar dataKey="แก้ว" radius={[8, 8, 0, 0]} maxBarSize={72}>
                  {barData.map((row) => (
                    <Cell key={row.ประเภท} fill={GROUP_COLORS[row.ประเภท] ?? '#78716c'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};
